/**
 * Wanders decorative roundels around their container via requestAnimationFrame
 * (a Lissajous curve) instead of a CSS keyframe loop, so the path never
 * retraces a fixed track. Position is driven through --roundel-x/-y/-rot.
 */
export class RoundelFloatController {
  constructor(root = document) {
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Optional mobile-only speed; tick() reads it live so breakpoint changes apply without a listener.
    this.mobileQuery = window.matchMedia("(max-width: 767.98px)");
    this.items = [];
    this.rafId = null;
    this.measureQueued = false;
    this.tick = this.tick.bind(this);
    this.observer =
      "IntersectionObserver" in window
        ? new IntersectionObserver((entries) => this.onIntersect(entries), { rootMargin: "120px" })
        : null;

    this.init(root);

    // React to the OS reduced-motion toggle, resizes, late layout, hidden tabs.
    this.motionQuery.addEventListener?.("change", () => this.reset());
    window.addEventListener("resize", () => this.scheduleMeasure(), { passive: true });
    window.addEventListener("load", () => this.scheduleMeasure(), { once: true });
    document.addEventListener("visibilitychange", () =>
      document.hidden ? this.stop() : this.start(),
    );
  }

  init(root = document) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    // Drop any roundels torn out of the DOM (e.g. a theme-editor re-render).
    this.items = this.items.filter((item) => item.el.isConnected);
    scope.querySelectorAll("[data-roundel-float]").forEach((el) => this.register(el));
    this.measureAll();
    this.reset();
  }

  register(el) {
    if (el.dataset.roundelBound === "true") return;
    el.dataset.roundelBound = "true";

    this.items.push({
      el,
      speed: parseFloat(el.dataset.roundelSpeed) || 12,
      // Optional mobile-only speed; falls back to `speed` when unset.
      mobileSpeed: parseFloat(el.dataset.roundelSpeedMobile) || null,
      // How far the roundel may drift past the container edges (px).
      bleed: parseFloat(el.dataset.roundelBleed) || 48,
      rest: el.dataset.roundelRest || "center",
      // De-sync multiple roundels so they don't move in lockstep.
      phase: Math.random() * Math.PI * 2,
      visible: true,
      bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
    });
    this.observer?.observe(el);
  }

  measureAll() {
    this.items.forEach((item) => this.measure(item));
  }

  measure(item) {
    const parent = item.el.offsetParent || item.el.parentElement;
    if (!parent) return;

    const cw = parent.clientWidth;
    const ch = parent.clientHeight;
    const rw = item.el.offsetWidth || 0;
    const rh = item.el.offsetHeight || 0;
    const { bleed } = item;

    item.bounds = {
      minX: -bleed,
      maxX: Math.max(-bleed, cw - rw + bleed),
      minY: -bleed,
      maxY: Math.max(-bleed, ch - rh + bleed),
    };
  }

  scheduleMeasure() {
    if (this.measureQueued) return;
    this.measureQueued = true;
    requestAnimationFrame(() => {
      this.measureQueued = false;
      this.measureAll();
      if (this.motionQuery.matches) this.parkAll();
    });
  }

  reset() {
    if (this.motionQuery.matches) {
      this.stop();
      this.parkAll();
    } else {
      this.start();
    }
  }

  parkAll() {
    this.items.forEach((item) => {
      const { minX, maxX, minY, maxY } = item.bounds;
      const atRight = item.rest.includes("right");
      const atBottom = item.rest.includes("bottom");
      const centred = item.rest === "center";
      const x = centred ? (minX + maxX) / 2 : atRight ? maxX : minX;
      const y = centred ? (minY + maxY) / 2 : atBottom ? maxY : minY;
      this.place(item, x, y, 0);
    });
  }

  place(item, x, y, rot) {
    item.el.style.setProperty("--roundel-x", `${x.toFixed(2)}px`);
    item.el.style.setProperty("--roundel-y", `${y.toFixed(2)}px`);
    item.el.style.setProperty("--roundel-rot", `${rot.toFixed(2)}deg`);
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      const item = this.items.find((candidate) => candidate.el === entry.target);
      if (item) item.visible = entry.isIntersecting;
    });
    if (this.items.some((item) => item.visible)) this.start();
    else this.stop();
  }

  tick(now) {
    const t = now / 1000; // seconds

    for (const item of this.items) {
      if (!item.visible) continue;

      const { phase, bounds } = item;
      const speed = this.mobileQuery.matches && item.mobileSpeed ? item.mobileSpeed : item.speed;
      const w = (Math.PI * 2) / speed; // base angular frequency

      // Irrational frequency ratio between the two sines → wanders instead of retracing one line.
      const u = (Math.sin(t * w + phase) + 1) / 2; // 0..1 across width
      const v = (Math.sin(t * w * 0.8 + phase * 1.7) + 1) / 2; // 0..1 across height

      const x = bounds.minX + (bounds.maxX - bounds.minX) * u;
      const y = bounds.minY + (bounds.maxY - bounds.minY) * v;
      const rot = Math.sin(t * w * 0.4 + phase) * 6; // subtle ±6° wobble

      this.place(item, x, y, rot);
    }

    this.rafId = requestAnimationFrame(this.tick);
  }

  start() {
    if (this.rafId !== null || this.motionQuery.matches) return;
    if (!this.items.some((item) => item.visible)) return;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(park = false) {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (park) {
      this.items.forEach((item) => {
        item.el.style.removeProperty("--roundel-x");
        item.el.style.removeProperty("--roundel-y");
        item.el.style.removeProperty("--roundel-rot");
      });
    }
  }

  destroy(root = document) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    const removed = new Set(scope.querySelectorAll("[data-roundel-float]"));
    removed.forEach((el) => {
      this.observer?.unobserve(el);
      el.dataset.roundelBound = "";
    });
    this.items = this.items.filter((item) => !removed.has(item.el));
    if (!this.items.length) this.stop();
  }
}

export const roundelFloatController = new RoundelFloatController(document);

if (typeof document !== "undefined") {
  document.addEventListener("shopify:section:load", (event) =>
    roundelFloatController.init(event?.target || document),
  );
  document.addEventListener("shopify:section:unload", (event) =>
    roundelFloatController.destroy(event?.target || document),
  );
}
